import { PROVIDER_CATEGORY_PREFIX } from 'root/util/constants';

const normalizeKey = (value) =>
    String(value ?? '')
        .trim()
        .toLowerCase();

const getProviderKeys = (item) => [item?.provider, item?.providerCode, item?.providerName, item?.name].filter(Boolean).map(normalizeKey);

const buildEnabledProviderIndex = (subCategoryMenus) => {
    const providersByCategory = {};
    const allProviders = new Set();

    Object.entries(subCategoryMenus).forEach(([categoryKey, menuProviders]) => {
        const categoryProviders = new Set();

        (menuProviders || []).forEach((menuProvider) => {
            getProviderKeys(menuProvider).forEach((providerKey) => {
                categoryProviders.add(providerKey);
                allProviders.add(providerKey);
            });
        });

        providersByCategory[categoryKey] = categoryProviders;
    });

    return { providersByCategory, allProviders };
};

const findCategoryKeyOnMenu = (game, providersByCategory) => {
    const aliasCategoryKey = PROVIDER_CATEGORY_PREFIX[game.category];
    if (aliasCategoryKey) return aliasCategoryKey;
    if (game.category && providersByCategory[game.category]) return game.category;

    const gameProviderKeys = getProviderKeys(game);
    const matchedCategoryKey = Object.keys(providersByCategory).find((categoryKey) => gameProviderKeys.some((key) => providersByCategory[categoryKey].has(key)));

    return matchedCategoryKey || null;
};

const getEnabledProviderScope = (game, { providersByCategory, allProviders }) => {
    const gameProviderKeys = getProviderKeys(game);
    const exactCategoryKey = findCategoryKeyOnMenu(game, providersByCategory);
    if (exactCategoryKey && providersByCategory[exactCategoryKey] && gameProviderKeys.some((key) => providersByCategory[exactCategoryKey].has(key))) return providersByCategory[exactCategoryKey];

    const matchedCategoryKey = Object.keys(providersByCategory).find((categoryKey) => gameProviderKeys.some((key) => providersByCategory[categoryKey].has(key)));

    return (matchedCategoryKey && providersByCategory[matchedCategoryKey]) || allProviders;
};

const isInSupportedProviderList = (game, supportedProviderList) => {
    if (!supportedProviderList) return true;
    const supportedKeys = new Set([...supportedProviderList].map(normalizeKey));

    return getProviderKeys(game).some((key) => supportedKeys.has(key));
};

const isGameProviderEnabled = (game, enabledProviderScope) => getProviderKeys(game).some((key) => enabledProviderScope.has(key));

const isFallbackTargetProviderEnabled = (game, enabledProviderScope) => {
    const targetProviders = [].concat(game.fallbackGameForProvider ?? []);

    return targetProviders.some((targetProvider) => enabledProviderScope.has(normalizeKey(targetProvider)));
};

export function isMenuSettingsReady(menuSlice) {
    const subCategoryMenus = menuSlice?.processedBaseMenuSettings?.subCategoryMenus;

    return Boolean(subCategoryMenus && Object.keys(subCategoryMenus).length);
}

export function filterHotGameListByMenuSettings(gameList, menuSlice, supportedProviderList, isFilterHotGames = true) {
    if (!isFilterHotGames) return gameList;

    return filterGamesByMenuSettings(gameList, menuSlice, supportedProviderList);
}

export function filterGamesByMenuSettings(gameList, menuSlice, supportedProviderList) {
    const subCategoryMenus = menuSlice?.processedBaseMenuSettings?.subCategoryMenus || {};
    const enabledProviderIndex = buildEnabledProviderIndex(subCategoryMenus);

    if (enabledProviderIndex.allProviders.size === 0) return gameList || [];

    return (gameList || []).filter((game) => {
        const enabledProviderScope = getEnabledProviderScope(game, enabledProviderIndex);

        if (!isInSupportedProviderList(game, supportedProviderList)) return false;

        const isProviderEnabled = isGameProviderEnabled(game, enabledProviderScope);
        const isFallbackRedundant = isFallbackTargetProviderEnabled(game, enabledProviderScope);

        return isProviderEnabled && !isFallbackRedundant;
    });
}
